import { ApolloLink } from "@apollo/client";
import { onError } from "@apollo/client/link/error";

import { ROUTE_PATH } from "src/core/constants/routePath";
import { tenantPaths } from "src/core/utils/route";

/**
 * Extension du type Error utilisée par Apollo pour les erreurs réseau.
 * `statusCode` est souvent présent pour les erreurs HTTP (500, 403, etc.)
 */
type NetworkError = Error & { statusCode?: number };

/**
 * Contexte custom pouvant être passé à une opération Apollo.
 *
 * - skipErrorLink : ignore complètement ce link (cas très spécifiques)
 * - suppressRedirect : empêche toute redirection automatique (ex: modales, retries maîtrisés)
 */
type OperationContext = {
  skipErrorLink?: boolean;
  suppressRedirect?: boolean;
};

/**
 * Extrait le tenantId depuis l'URL courante.
 *
 * Format attendu :
 *   /tenant/:tenantId/...
 *
 * Retourne `null` si :
 * - l'URL n'est pas tenant-scoped
 * - le tenantId est absent ou invalide
 *
 * ⚠️ Utilisé uniquement côté navigateur (window.location).
 */
const getTenantIdFromPathname = (): string | null => {
  const parts = window.location.pathname.split("/").filter(Boolean);

  // attendu: ["tenant", ":tenantId", ...]
  if (parts[0] !== "tenant") return null;

  const tenantId = parts[1];
  return tenantId && tenantId.length > 0 ? tenantId : null;
};

/**
 * Indique si l'utilisateur est déjà sur une page d'erreur.
 *
 * IMPORTANT :
 * - Empêche les boucles de redirection infinies
 * - Les pages d'erreur ne doivent JAMAIS rediriger vers elles-mêmes
 *
 * Gère :
 * - erreurs tenant-scoped : /tenant/:tenantId/error/xxx
 * - erreurs globales : /error/xxx
 */
const isOnErrorPage = (): boolean => {
  const p = window.location.pathname;

  // Tenant-scoped error pages
  if (/^\/tenant\/[^/]+\/error\/(403|404|500)(\/)?$/.test(p)) {
    return true;
  }

  // Global error pages (avec ou sans trailing slash)
  if (p === ROUTE_PATH.ERROR_403 || p === `${ROUTE_PATH.ERROR_403}/`) return true;
  if (p === ROUTE_PATH.ERROR_404 || p === `${ROUTE_PATH.ERROR_404}/`) return true;
  if (p === ROUTE_PATH.ERROR_500 || p === `${ROUTE_PATH.ERROR_500}/`) return true;

  return false;
};

/**
 * Redirection sécurisée vers une page d'erreur.
 *
 * Garde-fous importants :
 * - ne redirige pas si on est déjà sur la destination
 * - ne redirige pas depuis une page d'erreur
 * - évite les boucles infinies
 *
 * Utilise `window.location.replace` volontairement :
 * - on veut sortir de l'état applicatif courant (cache Apollo, retries, etc.)
 * - pas de retour possible via le bouton "back"
 */
const redirectSafely = (to: string) => {
  const current = window.location.pathname;

  // Déjà sur la bonne route (ou trailing slash)
  if (current === to || current === `${to}/`) return;

  // Ne jamais rediriger depuis une page d'erreur
  if (isOnErrorPage()) return;

  // setTimeout pour sortir du cycle Apollo
  setTimeout(() => {
    window.location.replace(to);
  }, 0);
};

/**
 * Construit la route d'erreur cible en fonction :
 * - du type d'erreur (403 / 404 / 500)
 * - du contexte courant (tenant-scoped ou global)
 *
 * Règle :
 * - si un tenantId est présent dans l'URL => erreur tenant-scoped
 * - sinon => erreur globale
 */
const toErrorPath = (kind: "403" | "404" | "500"): string => {
  const tenantId = getTenantIdFromPathname();

  if (tenantId) {
    if (kind === "403") {
      console.warn("[NAV] toErrorPath -> ERROR_403 (tenant-scoped)");
      return tenantPaths.error403(tenantId);
    }
    if (kind === "404") return tenantPaths.error404(tenantId);
    return tenantPaths.error500(tenantId);
  }

  if (kind === "403") {
    console.warn("[NAV] toErrorPath -> ERROR_403 (global)");
    return ROUTE_PATH.ERROR_403;
  }
  if (kind === "404") return ROUTE_PATH.ERROR_404;
  return ROUTE_PATH.ERROR_500;
};

/**
 * Apollo Link global de gestion des erreurs.
 *
 * Responsabilités :
 * - centraliser la gestion des erreurs réseau et GraphQL
 * - rediriger vers des pages d'erreur cohérentes (tenant / global)
 * - éviter les boucles de redirection
 *
 * ⚠️ Ce link doit rester :
 * - idempotent
 * - défensif
 * - sans dépendance React (pas de hooks ici)
 */
export const withErrorHandling = (): ApolloLink => {
  return onError(({ graphQLErrors, networkError, operation }) => {
    const ctx = (operation.getContext?.() ?? {}) as OperationContext;

    // L'opération demande explicitement d'ignorer ce link
    if (ctx.skipErrorLink) return;

    // L'opération ne souhaite pas de redirection automatique
    if (ctx.suppressRedirect) return;

    // On est déjà sur une page d'erreur => on ne fait rien
    if (isOnErrorPage()) return;

    /**
     * 1) ERREURS RÉSEAU (HTTP)
     */
    if (networkError) {
      const { statusCode } = networkError as NetworkError;

      console.error("[Apollo][Network error]", {
        op: operation.operationName,
        statusCode,
        error: networkError,
      });

      if (statusCode !== undefined && document.readyState !== "loading") {
        if (statusCode >= 500) {
          redirectSafely(toErrorPath("500"));
          return;
        }

        if (statusCode === 401 || statusCode === 403) {
          console.warn("[NAV] toErrorPath -> ERROR_403 (network error)");
          redirectSafely(toErrorPath("403"));
          return;
        }

        if (statusCode === 404) {
          redirectSafely(toErrorPath("404"));
          return;
        }
      }
    }

    /**
     * 2) ERREURS GRAPHQL
     */
    if (graphQLErrors?.length) {
      for (const err of graphQLErrors) {
        console.error("[Apollo][GraphQL error]", {
          op: operation.operationName,
          message: err.message,
          locations: err.locations,
          path: err.path,
          extensions: err.extensions,
        });
      }

      // Détection d'une erreur serveur "générique"
      const hasInternalServerError =
        graphQLErrors.some((e) => e.message === "Internal server error") ||
        graphQLErrors.some((e) => e.extensions?.code === "INTERNAL_SERVER_ERROR");

      if (hasInternalServerError && document.readyState !== "loading") {
        redirectSafely(toErrorPath("500"));
      }
    }
  });
};
